/**
 * GWP Manager – Gift-with-Purchase state management for the Stretch cart drawer.
 *
 * Reads configuration from a <script id="gwp-config" type="application/json"> block
 * embedded by the gwp-progress.liquid snippet. Listens to Stretch's cart events and
 * reconciles gift state (add / remove / swap / enforce-single) without infinite loops.
 */

(function () {
  'use strict';

  /* ====================================================================
     Helpers
     ==================================================================== */

  const ROUTES = Shopify.routes.root;

  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    const fmt =
      window.themeVariables?.settings?.moneyFormat ||
      window.__gwpConfig?.moneyFormat ||
      '€{{amount}}';
    const value = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return fmt
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount\s*\}\}/, value);
  }

  function resolveThreshold(tier, countryCode) {
    let overrides = {};
    try {
      overrides = typeof tier.overridesJson === 'string'
        ? JSON.parse(tier.overridesJson)
        : tier.overridesJson || {};
    } catch (_) { /* invalid JSON – use default */ }
    const raw = overrides[countryCode];
    if (raw != null) {
      const parsed = parseFloat(raw) * 100;
      if (!isNaN(parsed)) return parsed;
    }
    return tier.defaultThreshold;
  }

  /**
   * Sum of cart-level discount allocations, or null when the data is unreliable.
   * cart/change.js responses omit total_allocated_amount on discount applications,
   * so a cart carrying a discount code reports NaN — callers must refetch /cart.js.
   */
  function cartLevelDiscount(cart) {
    const apps = cart?.cart_level_discount_applications || [];
    let sum = 0;
    for (const d of apps) {
      const amount = Number(d?.total_allocated_amount);
      if (!Number.isFinite(amount)) return null;
      sum += amount;
    }
    return sum;
  }

  function hasReliableSubtotalData(cart) {
    return Array.isArray(cart?.items) && cartLevelDiscount(cart) !== null;
  }

  function cartSubtotal(cart) {
    let total = 0;
    for (const item of cart.items) {
      if (item.requires_shipping && item.properties?._gwp !== 'true') {
        total += Number(item.final_line_price) || 0;
      }
    }
    const discounts = cartLevelDiscount(cart) || 0;
    return Math.max(0, total - discounts);
  }

  function isRebuyPromoLine(item) {
    if (!item?.properties) return false;
    /* Paid Rebuy-attributed lines (upsells) are regular merchandise — matches cart-line-rebuy-promo.liquid. */
    if (item.final_line_price !== 0) return false;
    const props = item.properties;
    if (props._source === 'Rebuy') return true;
    if (props._widget_id) return true;
    if (props._attribution) return true;
    return false;
  }

  function isConfiguredGwpVariant(item, config) {
    if (!item || !config?.tiers?.length) return false;
    return config.tiers.some((t) => String(t.variantId) === String(item.variant_id));
  }

  /** Shoppable early-access merchandise — PDP adds with _early_access, never auto-GWP gifts. */
  function isEarlyAccessMerchandise(item, config) {
    if (!item) return false;
    if (item.properties?._early_access === 'true') return true;
    if (item.properties?._gwp === 'true') return false;

    const ids = config?.earlyAccessVariantIds || [];
    if (!ids.some((id) => String(id) === String(item.variant_id))) return false;

    /* Paid tier SKU bought from PDP (no gift properties) stays merchandise. */
    return item.final_line_price !== 0;
  }

  function hasGwpDiscountAllocation(item) {
    const allocs = item.line_level_discount_allocations || [];
    return allocs.some((a) => {
      const title = a.discount_application?.title || '';
      return /gift with purchase|gratis cadeau|cadeau offert|complimentary gift/i.test(title);
    });
  }

  /** Theme-managed gift lines — native _gwp, or free lines matching tier variants / the GWP discount. */
  function isGwpLine(item, config) {
    if (!item) return false;
    if (isEarlyAccessMerchandise(item, config)) return false;
    if (item.properties?._gwp === 'true') return true;
    /* Paid lines are never gifts — tier products can also be bought as regular merchandise. */
    if (item.final_line_price !== 0) return false;
    /* Free tier-variant line (incl. one added by Rebuy) is the gift — keeps dedupe working. */
    if (isConfiguredGwpVariant(item, config)) return true;
    /* Free Rebuy samples of other variants stay Rebuy-managed. */
    if (isRebuyPromoLine(item)) return false;
    if (hasGwpDiscountAllocation(item)) return true;
    return false;
  }

  function gwpItems(cart, config) {
    return cart.items.filter((i) => isGwpLine(i, config));
  }

  function qualifyingItems(cart, config) {
    return cart.items.filter((i) => !isGwpLine(i, config) && !isRebuyPromoLine(i));
  }

  function hasQualifyingMerchandise(cart, config) {
    return qualifyingItems(cart, config).length > 0;
  }

  function shouldRemoveGwpGifts(cart, config) {
    const gifts = gwpItems(cart, config);
    if (gifts.length === 0) return false;
    if (!hasQualifyingMerchandise(cart, config)) return true;

    const sub = cartSubtotal(cart);
    const cc = config.countryCode;
    const unlocked = config.tiers.filter((t) => sub >= resolveThreshold(t, cc));
    return unlocked.length === 0;
  }

  const GWP_ATTR_CLEAR = {
    gwp_selected_tier: '',
    gwp_selected_variant_id: '',
    gwp_applied_highest_tier: '',
    gwp_tier_manually_selected: '',
    _gwp_declined: '',
    _gwp_declined_tier: ''
  };

  /** Selection cleared but the customer's opt-out is kept, remembering which offer they declined. */
  function declinedAttrs(declinedTierKey) {
    return {
      ...GWP_ATTR_CLEAR,
      _gwp_declined: 'true',
      _gwp_declined_tier: declinedTierKey || ''
    };
  }

  function giftDeclined(cart) {
    return cart.attributes?._gwp_declined === 'true';
  }

  function highestUnlockedTier(cart, config) {
    const sub = cartSubtotal(cart);
    const unlocked = config.tiers.filter(
      (t) => sub >= resolveThreshold(t, config.countryCode)
    );
    return unlocked.length ? unlocked[unlocked.length - 1] : null;
  }

  function needsEnforcePass(cart, config) {
    const gifts = gwpItems(cart, config);
    return gifts.length > 1 || (gifts.length === 1 && gifts[0].quantity !== 1);
  }

  async function removeInvalidGwpGifts(config, cartInput, options = {}) {
    const cart = cartInput || await fetchCartJSON();
    if (!shouldRemoveGwpGifts(cart, config)) return cart;

    const gifts = gwpItems(cart, config);
    const updates = {};
    for (const item of gifts) updates[item.key] = 0;

    const hadAttrs = Boolean(
      cart.attributes?.gwp_selected_tier ||
      cart.attributes?.gwp_selected_variant_id
    );

    if (!Object.keys(updates).length && !hadAttrs) return cart;

    return cartBatchUpdate(
      updates,
      hadAttrs ? GWP_ATTR_CLEAR : undefined,
      options
    );
  }

  async function enforceSingleGift(config, cartInput, prefs = {}) {
    let cart = cartInput || await fetchCartJSON();
    let gifts = gwpItems(cart, config);

    if (gifts.length > 1) {
      let keeper;
      if (prefs.variantId) {
        keeper = gifts.find((g) => String(g.variant_id) === String(prefs.variantId));
      }
      if (!keeper && prefs.selectedVariantId) {
        keeper = gifts.find((g) => String(g.variant_id) === String(prefs.selectedVariantId));
      }
      if (!keeper) keeper = gifts[0];

      const updates = {};
      for (const g of gifts) {
        if (g !== keeper) updates[g.key] = 0;
      }
      if (Object.keys(updates).length) {
        cart = await cartBatchUpdate(updates);
        gifts = gwpItems(cart, config);
      }
    }

    if (gifts.length === 1 && gifts[0].quantity !== 1) {
      cart = await cartChange(gifts[0].key, 1);
    }

    return cart;
  }

  /* ====================================================================
     Cart API wrappers (return parsed JSON)
     ==================================================================== */

  async function cartAdd(variantId, tierKey) {
    const res = await fetch(`${ROUTES}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: [{
          id: Number(variantId),
          quantity: 1,
          properties: { _gwp: 'true', _gwp_tier: tierKey }
        }]
      })
    });
    if (!res.ok) throw new Error('cart/add failed: ' + res.status);
    return res.json();
  }

  async function cartChange(lineKey, quantity) {
    const res = await fetch(`${ROUTES}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity })
    });
    if (!res.ok) throw new Error('cart/change failed: ' + res.status);
    return res.json();
  }

  async function cartBatchUpdate(updates = {}, attributes, options = {}) {
    const body = {};
    if (updates && Object.keys(updates).length) body.updates = updates;
    if (attributes && Object.keys(attributes).length) body.attributes = attributes;
    if (options.sections?.length) body.sections = options.sections.join(',');

    const res = await fetch(`${ROUTES}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error('cart/update failed: ' + res.status);
    return res.json();
  }

  async function cartUpdateAttributes(attrs, options = {}) {
    return cartBatchUpdate({}, attrs, options);
  }

  async function fetchCartJSON() {
    const res = await fetch(`${ROUTES}cart.js`);
    return res.json();
  }

  async function fetchCartWithSections(sectionIds) {
    if (!sectionIds?.length) return fetchCartJSON();
    return cartBatchUpdate({}, null, { sections: sectionIds });
  }

  function getBundledSectionIds() {
    const sectionsToBundle = new Set();
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));
    return [...sectionsToBundle];
  }

  /* ====================================================================
     GWPManager
     ==================================================================== */

  class GWPManager {
    constructor() {
      this.config = null;
      this.reconciling = false;
      this.pendingReconcile = false;
      this._reconcileDebounce = null;
      this._reconcilePromise = null;
      this._needsRefresh = false;
      this._skipRefresh = false;
      this._pendingCartPageCart = null;
      this._pendingCartPageSections = null;
      this._latestCartFromEvent = null;
      this._refreshCart = null;
      this._pendingCartPageSync = null;
      this.upgradeDismissedFor = null; // tier key the user declined upgrading to
      this._checkoutNavigating = false;
      this._idleWaiters = [];
      this._lastCart = null;
      this._lastCartAt = 0;
      this._lastReconcileRunAt = 0;
      this._lastReconcileCleanAt = 0;
    }

    sectionOptions() {
      const sections = getBundledSectionIds();
      return sections.length ? { sections } : {};
    }

    stashRefreshCart(cart) {
      if (cart?.items) this._refreshCart = cart;
      this.rememberCart(cart);
    }

    rememberCart(cart) {
      if (cart?.items) {
        this._lastCart = cart;
        this._lastCartAt = Date.now();
      }
    }

    /** Recently-seen cart if fresh enough, otherwise a live fetch. */
    async getCart(maxAgeMs = 1500) {
      if (this._lastCart && Date.now() - this._lastCartAt < maxAgeMs) {
        return this._lastCart;
      }
      const cart = await fetchCartJSON();
      this.rememberCart(cart);
      return cart;
    }

    notifyIdle() {
      const waiters = this._idleWaiters;
      this._idleWaiters = [];
      waiters.forEach((resolve) => resolve());
    }

    async addGiftTier(tierCfg, options = {}, cart) {
      cart = await this.selectAndAdd(tierCfg, options, cart, this.sectionOptions());
      this.stashRefreshCart(cart);
      this._needsRefresh = true;
      return cart;
    }

    tierIndex(tierKey) {
      if (!tierKey || !this.config?.tiers) return -1;
      return this.config.tiers.findIndex((t) => t.key === tierKey);
    }

    resolveGiftTierKey(giftLine, selectedTier) {
      const prop = giftLine?.properties?._gwp_tier;
      if (prop) return prop;
      if (giftLine && this.config?.tiers) {
        const match = this.config.tiers.find(
          (t) => String(t.variantId) === String(giftLine.variant_id)
        );
        if (match) return match.key;
      }
      return selectedTier || '';
    }

    /* ── Bootstrap ─────────────────────────────────────────────────── */

    init() {
      this.readConfig();

      document.addEventListener('cart:prepare-bundled-sections', (event) => {
        if (!this.onCartPage()) return;
        const form = document.getElementById('cart-main-form');
        const section = form?.closest('.shopify-section');
        if (!section?.id) return;
        event.detail.sections.add(section.id.replace('shopify-section-', ''));
      });

      document.addEventListener('cart:change', (e) => this.onCartChange(e));
      document.addEventListener('cart:refresh', () => {
        if (this.onCartPage() && !this.reconciling && !this._cartPageRefreshing) {
          this.applyCartRefresh().catch((e) => console.warn('[GWP] Cart refresh failed', e));
        }
      });
      document.addEventListener('cart-drawer:refreshed', () => this.onDrawerRefreshed());
      document.addEventListener('click', (e) => this.onClick(e));
      // Capture phase — must run before the theme's LineItem delegate handles the cart/change link.
      document.addEventListener('click', (e) => this.onGiftRemoveClick(e), true);
      document.addEventListener('click', (e) => this.onCheckoutIntent(e), true);

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.config && this.reconcileCooldownOver()) {
          this.initialReconcile();
        }
      });

      window.addEventListener('pageshow', (event) => {
        // Only bfcache restores need a re-check; init already reconciles on normal loads.
        if (event.persisted && this.config) {
          this.initialReconcile();
        }
      });

      window.addEventListener('focus', () => {
        if (this.config && this.onCartPage() && this.reconcileCooldownOver()) {
          this.initialReconcile();
        }
      });

      // Guard must exist even when config arrives later (e.g. session starts with empty cart).
      this.bindCheckoutGuard();

      if (this.config) {
        this.initialReconcile();
      } else if (this.onCartPage()) {
        // Cart page: progress + config render with the page; reconcile after paint
        queueMicrotask(() => {
          if (!this.config) this.readConfig();
          if (this.config) {
            this.initialReconcile();
          }
        });
      }
    }

    reconcileCooldownOver() {
      return Date.now() - this._lastReconcileRunAt > 1500;
    }

    bindCheckoutGuard() {
      if (this._checkoutGuardBound) return;
      this._checkoutGuardBound = true;

      document.addEventListener(
        'submit',
        async (event) => {
          if (this._checkoutNavigating || !this.config) return;

          const form = event.target;
          if (!form?.action || !String(form.action).includes('/cart')) return;

          const submitter = event.submitter || document.activeElement;
          if (!submitter || submitter.name !== 'checkout') return;

          event.preventDefault();
          event.stopImmediatePropagation();

          const checkoutBtn = submitter;
          const checkoutLabel = checkoutBtn.textContent;
          checkoutBtn.setAttribute('aria-busy', 'true');
          checkoutBtn.disabled = true;

          let navigated = false;

          try {
            this.setCheckoutBusy(true);

            await Promise.race([
              this.waitForReconcile(),
              new Promise((resolve) => setTimeout(resolve, 6000))
            ]);

            this._checkoutNavigating = true;

            // Recent clean reconcile (no mutations needed) — cart already valid, skip extra round trips.
            let cart = null;
            if (Date.now() - this._lastReconcileCleanAt < 2500) {
              cart = this._lastCart;
            }
            if (!cart) cart = await this.ensureGwpForCheckout();

            if (gwpItems(cart, this.config).length > 0 && !hasQualifyingMerchandise(cart, this.config)) {
              console.warn('[GWP] Blocked checkout — orphan gift in cart');
              this._checkoutNavigating = false;
              this._needsRefresh = true;
              await this.applyCartRefresh(cart);
              return;
            }

            window.location.assign(`${ROUTES}checkout`);
            navigated = true;
          } catch (e) {
            console.warn('[GWP] Checkout ensure failed', e);
            window.location.assign(`${ROUTES}checkout`);
            navigated = true;
          } finally {
            this._checkoutNavigating = false;
            if (!navigated) {
              checkoutBtn.removeAttribute('aria-busy');
              checkoutBtn.disabled = false;
              if (checkoutLabel) checkoutBtn.textContent = checkoutLabel;
              this.setCheckoutBusy(false);
            }
          }
        },
        true
      );
    }

    onCartPage() {
      return (
        window.themeVariables?.settings?.pageType === 'cart' ||
        Boolean(document.getElementById('cart-main-form'))
      );
    }

    readConfig() {
      const el = document.getElementById('gwp-config');
      if (!el) return;
      try {
        this.config = JSON.parse(el.textContent);
      } catch (_) {
        console.warn('[GWP] Failed to parse config');
      }
    }

    /* ── Events ────────────────────────────────────────────────────── */

    onCartChange(event) {
      if (!event.detail?.cart) return;
      if (event.detail?.baseEvent === 'gwp:reconcile') return;

      const cart = event.detail.cart;
      const isLineItemChange = event.detail?.baseEvent === 'line-item:change';

      if (this.onCartPage() && isLineItemChange) {
        this._pendingCartPageSync = cart;
        if (cart.sections) {
          const sectionIds = Object.keys(cart.sections);
          this.patchCartPage(cart, sectionIds).catch((e) => {
            console.warn('[GWP] Cart page qty patch failed', e);
          });
        }
      }

      if (!this.config) this.readConfig();
      if (!this.config) {
        this._pendingCartPageSync = null;
        return;
      }

      this._latestCartFromEvent = cart;
      this.rememberCart(cart);

      const immediate =
        isLineItemChange ||
        event.detail?.baseEvent === 'variant:add';

      this.scheduleReconcile({ immediate });
    }

    onCheckoutIntent(event) {
      if (!this.config || this._checkoutNavigating) return;
      const btn = event.target.closest('[name="checkout"]');
      if (!btn || btn.disabled) return;
      // Prefetch only — must not disable the button (capture runs before submit).
      this.scheduleReconcile({ immediate: true, skipRefresh: true });
    }

    scheduleReconcile({ immediate = false, skipRefresh = false } = {}) {
      if (skipRefresh) this._skipRefresh = true;

      clearTimeout(this._reconcileDebounce);

      if (this._reconcilePromise) {
        this.pendingReconcile = true;
        return this._reconcilePromise;
      }

      if (immediate) {
        return this.runReconcile();
      }

      this._reconcileDebounce = setTimeout(() => {
        this._reconcileDebounce = null;
        this.runReconcile();
      }, 32);
    }

    runReconcile() {
      if (this._reconcilePromise) return this._reconcilePromise;

      this._lastReconcileRunAt = Date.now();
      this._reconcilePromise = this.reconcile()
        .catch((e) => console.warn('[GWP] Reconcile failed', e))
        .finally(() => {
          this._reconcilePromise = null;
        });

      return this._reconcilePromise;
    }

    async waitForReconcile() {
      clearTimeout(this._reconcileDebounce);
      this._reconcileDebounce = null;

      if (this._reconcilePromise) {
        await this._reconcilePromise;
      } else if (!this.reconciling) {
        this._skipRefresh = true;
        await this.runReconcile();
      } else {
        // Busy with a tier select/switch — wait for it to signal idle.
        await new Promise((resolve) => {
          this._idleWaiters.push(resolve);
          setTimeout(resolve, 8000);
        });
      }

      if (this.pendingReconcile) {
        this.pendingReconcile = false;
        this._skipRefresh = true;
        await this.runReconcile();
      }
    }

    async ensureGwpForCheckout() {
      let cart = await fetchCartJSON();
      this.rememberCart(cart);

      if (shouldRemoveGwpGifts(cart, this.config)) {
        return removeInvalidGwpGifts(this.config, cart);
      }

      /* Declined and the offer is unchanged — make sure no gift goes to checkout, never auto-add.
         (A changed offer is handled by reconcile before the guard reaches this point.) */
      if (giftDeclined(cart)) {
        const declinedTierKey = cart.attributes?._gwp_declined_tier || '';
        const currentHighest = highestUnlockedTier(cart, this.config);

        if ((currentHighest?.key || '') === declinedTierKey) {
          const declinedGifts = gwpItems(cart, this.config);
          if (declinedGifts.length) {
            const updates = {};
            for (const g of declinedGifts) updates[g.key] = 0;
            cart = await cartBatchUpdate(updates, declinedAttrs(declinedTierKey));
            this.rememberCart(cart);
          }
          return cart;
        }
        /* offer changed — fall through to the normal add path below */
      }

      let gifts = gwpItems(cart, this.config);
      if (gifts.length === 0) {
        const sub = cartSubtotal(cart);
        const cc = this.config.countryCode;
        const unlocked = this.config.tiers.filter((t) => sub >= resolveThreshold(t, cc));
        const highestUnlocked = unlocked.length ? unlocked[unlocked.length - 1] : null;
        if (highestUnlocked) {
          return this.selectAndAdd(highestUnlocked, {
            appliedHighest: highestUnlocked.key,
            manual: cart.attributes?.gwp_tier_manually_selected === 'true'
          }, cart);
        }
        return cart;
      }

      cart = await enforceSingleGift(this.config, cart, {
        selectedVariantId: cart.attributes?.gwp_selected_variant_id || '',
        variantId: gifts.length === 1 ? gifts[0].variant_id : undefined
      });
      return cart;
    }

    setCheckoutBusy(busy) {
      document.querySelectorAll('[name="checkout"]').forEach((btn) => {
        btn.toggleAttribute('aria-busy', busy);
        btn.disabled = busy;
      });
    }

    onDrawerRefreshed() {
      this.readConfig();
      this.evaluateUpgradePrompt();
      this.syncSelectorFromCart();
      if (this.config && !this.reconciling) {
        this.scheduleReconcile({ immediate: true });
      }
    }

    async syncSelectorFromCart() {
      if (!this.config || this.config.allowCustomerTierChoice !== true) return;
      try {
        const cart = await this.getCart(2000);
        const sub = cartSubtotal(cart);
        const cc = this.config.countryCode;
        const unlocked = this.config.tiers.filter((t) => sub >= resolveThreshold(t, cc));
        const highestUnlocked = unlocked.length ? unlocked[unlocked.length - 1] : null;
        const gifts = gwpItems(cart, this.config);
        if (gifts.length !== 1) return;
        const giftTierKey = this.resolveGiftTierKey(
          gifts[0],
          cart.attributes?.gwp_selected_tier || ''
        );
        if (!giftTierKey) return;
        await this.finalizeSelectorState(cart, giftTierKey, highestUnlocked);
      } catch (e) {
        console.warn('[GWP] Selector sync failed', e);
      }
    }

    onGiftRemoveClick(event) {
      const link = event.target.closest('[data-gwp-remove]');
      if (!link) return;
      event.preventDefault();
      event.stopPropagation();
      this.handleGiftRemove();
    }

    async handleGiftRemove() {
      if (!this.config) this.readConfig();
      if (!this.config || this.reconciling) return;

      this.reconciling = true;
      this.setLoading(true);
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const cart = await this.getCart();
        const gifts = gwpItems(cart, this.config);
        const currentHighest = highestUnlockedTier(cart, this.config);

        const updates = {};
        for (const g of gifts) updates[g.key] = 0;

        const after = await cartBatchUpdate(
          updates,
          declinedAttrs(currentHighest?.key),
          this.sectionOptions()
        );
        this.stashRefreshCart(after);
        this.pendingUpgradeTier = null;
        this.upgradeDismissedFor = null;
        this.syncSelectorUI('');
        await this.applyCartRefresh(after);
        this._refreshCart = null;
      } catch (e) {
        console.warn('[GWP] Gift remove failed', e);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        this.reconciling = false;
        this.setLoading(false);
        this.notifyIdle();
      }
    }

    /** "Add gift back" prompt — re-opt into the currently unlocked offer. */
    async handleGiftReAdd(button) {
      if (!this.config) this.readConfig();
      if (!this.config || this.reconciling) return;

      this.reconciling = true;
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const cart = await this.getCart();
        const highest = highestUnlockedTier(cart, this.config);

        if (!highest) {
          // No longer eligible — refresh so the prompt disappears.
          await this.applyCartRefresh();
          return;
        }

        await this.addGiftTier(highest, { appliedHighest: highest.key }, cart);
        await this.applyCartRefresh(this._refreshCart);
        this._refreshCart = null;
      } catch (e) {
        console.warn('[GWP] Gift re-add failed', e);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
        this.reconciling = false;
        this.notifyIdle();
      }
    }

    onClick(event) {
      const readdEl = event.target.closest('[data-gwp-readd]');
      if (readdEl) {
        // The data attribute sits on a wrapper around the theme button snippet.
        const readdBtn = readdEl.matches('button') ? readdEl : readdEl.querySelector('button');
        if (readdBtn?.disabled) return;
        event.preventDefault();
        this.handleGiftReAdd(readdBtn);
        return;
      }

      if (this.config?.allowCustomerTierChoice !== true) return;
      const selectBtn = event.target.closest('[data-gwp-select-tier]');
      if (selectBtn && !selectBtn.disabled) {
        event.preventDefault();
        this.handleTierSelect(selectBtn.dataset.gwpSelectTier);
        return;
      }

      if (event.target.closest('[data-gwp-keep]')) {
        event.preventDefault();
        if (this.config?.showUpgradePrompt !== true) return;
        this.handleKeep();
        return;
      }

      if (event.target.closest('[data-gwp-switch]')) {
        event.preventDefault();
        if (this.config?.showUpgradePrompt !== true) return;
        this.handleSwitch();
        return;
      }
    }

    /* ── Reconcile ────────────────────────────────────────────────── */

    async initialReconcile() {
      try {
        await this.runReconcile();
      } catch (e) {
        console.warn('[GWP] Initial reconcile failed', e);
      }
    }

    async reconcile() {
      if (!this.config) return;
      if (this.reconciling) {
        this.pendingReconcile = true;
        return;
      }
      this.reconciling = true;
      this._needsRefresh = false;

      const allowChoice = this.config.allowCustomerTierChoice === true;

      const refreshOpts = this.sectionOptions();
      let hadError = false;

      try {
        let cart = this._latestCartFromEvent;
        this._latestCartFromEvent = null;
        /* cart/change.js omits cart-level discount amounts — refetch /cart.js so the
           subtotal (and therefore the unlocked tier) is accurate when a code is applied. */
        if (!hasReliableSubtotalData(cart)) cart = await fetchCartJSON();
        this.rememberCart(cart);

        /* Customer removed the gift — honor it: no auto-add, drop any gift that sneaks back in.
           The opt-out is tied to the offer they declined: when cart changes unlock a different
           tier, the decline resets and the new gift is offered through the normal flow below.
           It also resets once the cart has no qualifying merchandise left (fresh cart). */
        if (giftDeclined(cart)) {
          const stillQualifying = hasQualifyingMerchandise(cart, this.config);
          const declinedTierKey = cart.attributes?._gwp_declined_tier || '';
          const currentHighest = highestUnlockedTier(cart, this.config);
          const offerChanged =
            stillQualifying && (currentHighest?.key || '') !== declinedTierKey;

          if (!offerChanged) {
            const declinedGifts = gwpItems(cart, this.config);

            if (declinedGifts.length || !stillQualifying) {
              const updates = {};
              for (const g of declinedGifts) updates[g.key] = 0;
              cart = await cartBatchUpdate(
                updates,
                stillQualifying ? declinedAttrs(declinedTierKey) : GWP_ATTR_CLEAR,
                refreshOpts
              );
              this.stashRefreshCart(cart);
              if (declinedGifts.length) this._needsRefresh = true;
            }

            this.pendingUpgradeTier = null;
            this.syncSelectorUI('');
            return;
          }
          /* offer changed — fall through; addGiftTier/selectAndAdd clears the declined attrs */
        }

        if (needsEnforcePass(cart, this.config)) {
          cart = await enforceSingleGift(this.config, cart, {
            selectedVariantId: cart.attributes?.gwp_selected_variant_id || ''
          });
          this.rememberCart(cart);
          this._needsRefresh = true;
        }

        const sub = cartSubtotal(cart);
        const cc = this.config.countryCode;
        let gifts = gwpItems(cart, this.config);
        let selectedTier = cart.attributes?.gwp_selected_tier || '';
        let selectedVariantId = cart.attributes?.gwp_selected_variant_id || '';

        const unlockedTiers = this.config.tiers.filter((t) => sub >= resolveThreshold(t, cc));
        const unlockedKeys = unlockedTiers.map((t) => t.key);
        const highestUnlocked = unlockedTiers.length ? unlockedTiers[unlockedTiers.length - 1] : null;

        if (shouldRemoveGwpGifts(cart, this.config)) {
          const hadGifts = gifts.length > 0;
          const hadAttrs = Boolean(selectedTier || selectedVariantId);
          cart = await removeInvalidGwpGifts(this.config, cart, refreshOpts);
          this.stashRefreshCart(cart);
          gifts = gwpItems(cart, this.config);
          if (hadGifts || hadAttrs || gifts.length) this._needsRefresh = true;
          return;
        }

        /* Tier unlocked but gift line missing — add even when the same SKU is already paid early-access merchandise. */
        if (gifts.length === 0 && highestUnlocked) {
          await this.addGiftTier(highestUnlocked, {
            appliedHighest: highestUnlocked.key,
            manual: cart.attributes?.gwp_tier_manually_selected === 'true'
          }, cart);
          return;
        }

        const appliedHighest = cart.attributes?.gwp_applied_highest_tier || '';
        const manuallySelected = cart.attributes?.gwp_tier_manually_selected === 'true';
        const giftTierKey = gifts.length === 1
          ? this.resolveGiftTierKey(gifts[0], selectedTier)
          : '';

        if (allowChoice) {
          const giftInvalid = giftTierKey && !unlockedKeys.includes(giftTierKey);
          const selectedInvalid = selectedTier && !unlockedKeys.includes(selectedTier);

          if (giftInvalid || selectedInvalid) {
            if (highestUnlocked) {
              await this.addGiftTier(highestUnlocked, { appliedHighest: highestUnlocked.key }, cart);
            } else if (gifts.length) {
              const updates = {};
              for (const g of gifts) updates[g.key] = 0;
              cart = await cartBatchUpdate(updates, GWP_ATTR_CLEAR, refreshOpts);
              this.stashRefreshCart(cart);
              this._needsRefresh = true;
            }
            return;
          }

          if (gifts.length === 1 && highestUnlocked && giftTierKey) {
            const highIdx = this.tierIndex(highestUnlocked.key);
            const giftIdx = this.tierIndex(giftTierKey);

            if (highIdx > giftIdx) {
              const userOptedDown =
                manuallySelected &&
                selectedTier === giftTierKey &&
                this.tierIndex(selectedTier) < highIdx &&
                appliedHighest === highestUnlocked.key;

              if (userOptedDown) {
                const tierCfg = this.config.tiers.find((t) => t.key === selectedTier);
                if (tierCfg) {
                  const giftMatches = String(gifts[0].variant_id) === String(tierCfg.variantId);
                  const attrsMatch = String(selectedVariantId) === String(tierCfg.variantId);
                  if (!giftMatches || !attrsMatch) {
                    await this.addGiftTier(tierCfg, {
                      manual: true,
                      appliedHighest: highestUnlocked.key
                    }, cart);
                    return;
                  }
                }

                if (
                  this.config.showUpgradePrompt === true &&
                  this.upgradeDismissedFor !== highestUnlocked.key
                ) {
                  this.pendingUpgradeTier = highestUnlocked;
                  this._needsRefresh = true;
                  return;
                }

                this.pendingUpgradeTier = null;
                this.syncSelectorUI(giftTierKey || selectedTier);
                return;
              }

              await this.addGiftTier(highestUnlocked, { appliedHighest: highestUnlocked.key }, cart);
              this.pendingUpgradeTier = null;
              return;
            }
          }

          if (selectedTier && unlockedKeys.includes(selectedTier)) {
            const tierCfg = this.config.tiers.find((t) => t.key === selectedTier);
            if (tierCfg) {
              const giftMatches =
                gifts.length === 1 &&
                String(gifts[0].variant_id) === String(tierCfg.variantId);
              const attrsMatch =
                String(selectedVariantId) === String(tierCfg.variantId);

              if (!giftMatches || !attrsMatch) {
                if (
                  gifts.length === 1 &&
                  giftTierKey &&
                  unlockedKeys.includes(giftTierKey) &&
                  giftTierKey !== selectedTier
                ) {
                  const updatedCart = await this.finalizeSelectorState(
                    cart,
                    giftTierKey,
                    highestUnlocked,
                    refreshOpts
                  );
                  if (updatedCart) {
                    this.stashRefreshCart(updatedCart);
                    this._needsRefresh = true;
                  }
                  return;
                }

                await this.addGiftTier(tierCfg, {
                  manual: manuallySelected,
                  appliedHighest: highestUnlocked?.key || appliedHighest || selectedTier
                }, cart);
                return;
              }
            }
          }

          if (
            gifts.length === 1 &&
            highestUnlocked &&
            giftTierKey === highestUnlocked.key
          ) {
            const attrsOk =
              selectedTier === highestUnlocked.key &&
              String(selectedVariantId) === String(highestUnlocked.variantId);
            if (!attrsOk) {
              const updatedCart = await this.finalizeSelectorState(
                cart,
                giftTierKey,
                highestUnlocked,
                refreshOpts
              );
              if (updatedCart) {
                this.stashRefreshCart(updatedCart);
                this._needsRefresh = true;
              }
              return;
            }
          }

          this.pendingUpgradeTier = null;
          if (giftTierKey) {
            this.syncSelectorUI(giftTierKey);
          }
        } else if (highestUnlocked) {
          const giftVariantOk =
            gifts.length === 1 &&
            String(gifts[0].variant_id) === String(highestUnlocked.variantId);
          const attrsOk =
            selectedTier === highestUnlocked.key &&
            String(selectedVariantId) === String(highestUnlocked.variantId);

          if (!giftVariantOk || !attrsOk) {
            await this.addGiftTier(highestUnlocked, { appliedHighest: highestUnlocked.key }, cart);
            return;
          }

          this.pendingUpgradeTier = null;
          return;
        }

        if (!allowChoice && !selectedTier && unlockedKeys.length && highestUnlocked) {
          await this.addGiftTier(highestUnlocked, { appliedHighest: highestUnlocked.key }, cart);
          return;
        }
      } catch (e) {
        hadError = true;
        console.warn('[GWP] Reconcile error', e);
      } finally {
        const shouldRefresh = this._needsRefresh;
        const skipRefresh = this._skipRefresh;
        this._needsRefresh = false;
        this._skipRefresh = false;
        this.reconciling = false;
        this._lastReconcileCleanAt = !shouldRefresh && !hadError ? Date.now() : 0;
        this.notifyIdle();

        if (shouldRefresh) {
          const cartForRefresh = this._refreshCart;
          this._refreshCart = null;
          this._pendingCartPageSync = null;
          const refresh = () => this.applyCartRefresh(cartForRefresh).catch((e) => {
            console.warn('[GWP] Cart refresh failed', e);
          });
          if (skipRefresh) {
            queueMicrotask(refresh);
          } else {
            try {
              await refresh();
            } catch (_) { /* logged above */ }
          }
        } else if (this.onCartPage() && this._pendingCartPageSync) {
          const cartForSync = this._pendingCartPageSync;
          this._pendingCartPageSync = null;
          if (!cartForSync.sections) {
            try {
              await this.applyCartRefresh(cartForSync);
            } catch (e) {
              console.warn('[GWP] Cart page sync failed', e);
            }
          }
        } else {
          this._pendingCartPageSync = null;
        }

        if (this.pendingReconcile) {
          this.pendingReconcile = false;
          queueMicrotask(() => this.runReconcile());
        }
      }
    }

    /* ── Gift operations ──────────────────────────────────────────── */

    async syncCartAttributesFromGift(giftLine, giftTierKey, options = {}, cartInput, requestOpts = {}) {
      const tierCfg = this.config.tiers.find((t) => t.key === giftTierKey);
      if (!tierCfg || !giftLine) return null;

      const cart = cartInput || await fetchCartJSON();
      const selectedTier = cart.attributes?.gwp_selected_tier || '';
      const selectedVariantId = cart.attributes?.gwp_selected_variant_id || '';
      const attrsOk =
        selectedTier === giftTierKey &&
        String(selectedVariantId) === String(tierCfg.variantId);
      if (attrsOk) return null;

      const attrs = {
        gwp_selected_tier: giftTierKey,
        gwp_selected_variant_id: tierCfg.variantId
      };
      if (options.appliedHighest) {
        attrs.gwp_applied_highest_tier = options.appliedHighest;
      }
      return cartUpdateAttributes(attrs, requestOpts);
    }

    syncSelectorUI(selectedTierKey) {
      const selector = document.getElementById('gwp-selector');
      if (selector) {
        selector.querySelectorAll('[data-gwp-select-tier]').forEach((btn) => {
          btn.classList.toggle(
            'gwp-selector__card--selected',
            btn.dataset.gwpSelectTier === selectedTierKey
          );
        });
      }

      const progress = document.getElementById('gwp-progress');
      if (progress) {
        progress.dataset.gwpSelectedTier = selectedTierKey || '';
      }
    }

    async finalizeSelectorState(cart, giftTierKey, highestUnlocked, requestOpts = {}) {
      const effectiveTier = giftTierKey || cart.attributes?.gwp_selected_tier || '';
      if (!effectiveTier) return null;

      let updatedCart = null;
      if (giftTierKey) {
        const gifts = gwpItems(cart, this.config);
        if (gifts.length === 1) {
          updatedCart = await this.syncCartAttributesFromGift(
            gifts[0],
            giftTierKey,
            { appliedHighest: highestUnlocked?.key || cart.attributes?.gwp_applied_highest_tier || giftTierKey },
            cart,
            requestOpts
          );
        }
      }

      this.syncSelectorUI(effectiveTier);
      return updatedCart;
    }

    async selectAndAdd(tierCfg, options = {}, cartInput, requestOpts = {}) {
      const cart = cartInput || await this.getCart();
      const gifts = gwpItems(cart, this.config);
      const sectionIds = requestOpts.sections;

      const attrs = {
        gwp_selected_tier: tierCfg.key,
        gwp_selected_variant_id: tierCfg.variantId,
        gwp_tier_manually_selected: options.manual ? 'true' : '',
        // adding a gift re-opts into the offer
        _gwp_declined: '',
        _gwp_declined_tier: ''
      };
      if (options.appliedHighest) {
        attrs.gwp_applied_highest_tier = options.appliedHighest;
      }

      /* Add first (skipped when the right gift is already in the cart), then one update.js
         call removes the old gift, writes attributes and returns cart + bundled sections. */
      const existing = gifts.find(
        (g) => String(g.variant_id) === String(tierCfg.variantId)
      );
      if (!existing) {
        await cartAdd(tierCfg.variantId, tierCfg.key);
      }

      const updates = {};
      for (const g of gifts) {
        if (g !== existing) updates[g.key] = 0;
      }
      if (existing && existing.quantity !== 1) updates[existing.key] = 1;

      let after = await cartBatchUpdate(
        updates,
        attrs,
        sectionIds?.length ? { sections: sectionIds } : {}
      );

      if (needsEnforcePass(after, this.config)) {
        after = await enforceSingleGift(this.config, after, { variantId: tierCfg.variantId });
        if (sectionIds?.length && !after.sections) {
          after = await fetchCartWithSections(sectionIds);
        }
      }

      this.rememberCart(after);
      this.syncSelectorUI(tierCfg.key);
      return after;
    }

    async handleTierSelect(tierKey) {
      if (!this.config || this.config.allowCustomerTierChoice !== true) return;
      if (this.reconciling) return;

      /* Resolve from config — never trust variant ids coming from the DOM. */
      const tierCfg = this.config.tiers.find((t) => t.key === tierKey);
      if (!tierCfg) return;

      const previousTier = document.getElementById('gwp-progress')?.dataset.gwpSelectedTier || '';
      this.reconciling = true;
      this.setLoading(true, tierKey);
      this.syncSelectorUI(tierKey);

      try {
        const cart = await this.getCart();
        const sub = cartSubtotal(cart);
        const cc = this.config.countryCode;
        const unlocked = this.config.tiers.filter((t) => sub >= resolveThreshold(t, cc));
        const highestUnlocked = unlocked.length ? unlocked[unlocked.length - 1] : null;

        /* Stale DOM (subtotal dropped since render) — re-render selector instead of adding a locked tier. */
        if (!unlocked.some((t) => t.key === tierKey)) {
          this.syncSelectorUI(previousTier);
          await this.applyCartRefresh();
          return;
        }

        await this.addGiftTier(
          tierCfg,
          { manual: true, appliedHighest: highestUnlocked?.key || tierKey },
          cart
        );
        this.upgradeDismissedFor = null;
        await this.applyCartRefresh(this._refreshCart);
        this._refreshCart = null;
      } catch (e) {
        console.warn('[GWP] Select tier failed', e);
        this.syncSelectorUI(previousTier);
      } finally {
        this.reconciling = false;
        this.setLoading(false);
        this.notifyIdle();
      }
    }

    handleKeep() {
      const prompt = document.getElementById('gwp-upgrade-prompt');
      if (prompt) prompt.hidden = true;
      if (this.pendingUpgradeTier) {
        this.upgradeDismissedFor = this.pendingUpgradeTier.key;
      }
      this.pendingUpgradeTier = null;
    }

    async handleSwitch() {
      if (!this.pendingUpgradeTier || this.reconciling) return;
      const tier = this.pendingUpgradeTier;
      this.pendingUpgradeTier = null;
      this.upgradeDismissedFor = null;

      this.reconciling = true;
      this.setLoading(true, tier.key);
      this.syncSelectorUI(tier.key);
      try {
        await this.addGiftTier(tier, { appliedHighest: tier.key });
        await this.applyCartRefresh(this._refreshCart);
        this._refreshCart = null;
      } catch (e) {
        console.warn('[GWP] Switch tier failed', e);
      } finally {
        this.reconciling = false;
        this.setLoading(false);
        this.notifyIdle();
      }
    }

    /* ── UI updates ───────────────────────────────────────────────── */

    async applyCartRefresh(cartInput) {
      if (this._checkoutNavigating) return;

      const sectionIds = getBundledSectionIds();
      let cart = cartInput;

      if (!cart?.sections && sectionIds.length) {
        cart = await fetchCartWithSections(sectionIds);
      } else if (!cart) {
        cart = sectionIds.length
          ? await fetchCartWithSections(sectionIds)
          : await fetchCartJSON();
      }
      this.rememberCart(cart);

      if (this.onCartPage()) {
        await this.patchCartPage(cart, sectionIds);
        return;
      }

      if (cart?.sections && sectionIds.length) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'gwp:reconcile', cart }
        }));
        document.dispatchEvent(new CustomEvent('cart-drawer:refreshed', { bubbles: true }));
        return;
      }

      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    async patchCartPage(cart, sectionIds) {
      if (this._cartPageRefreshing) {
        this._pendingCartPageCart = cart;
        this._pendingCartPageSections = sectionIds;
        return;
      }
      this._cartPageRefreshing = true;

      const form = document.getElementById('cart-main-form');
      const section = form?.closest('.shopify-section');
      if (!section) {
        this._cartPageRefreshing = false;
        window.location.reload();
        return;
      }

      const sectionId = section.id.replace('shopify-section-', '');
      const noteInput = section.querySelector('cart-note [name="note"]');
      const noteValue = noteInput?.value;

      try {
        const sectionHtml = cart?.sections?.[sectionId];
        if (sectionHtml) {
          const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
          const newSection = doc.getElementById(`shopify-section-${sectionId}`);
          if (newSection) {
            section.innerHTML = newSection.innerHTML;
          } else {
            window.location.reload();
            return;
          }
        } else {
          const html = await (await fetch(`${ROUTES}?section_id=${sectionId}`)).text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newSection = doc.getElementById(`shopify-section-${sectionId}`);
          if (!newSection) {
            window.location.reload();
            return;
          }
          section.innerHTML = newSection.innerHTML;
        }

        if (noteValue != null) {
          const restoredNote = section.querySelector('cart-note [name="note"]');
          if (restoredNote) restoredNote.value = noteValue;
        }

        this.readConfig();
        document.dispatchEvent(new CustomEvent('cart-drawer:refreshed', { bubbles: true }));

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'gwp:reconcile', cart: cart || await fetchCartJSON() }
        }));
      } catch (e) {
        console.warn('[GWP] Cart page refresh failed', e);
        window.location.reload();
      } finally {
        this._cartPageRefreshing = false;
        if (this._pendingCartPageCart) {
          const pendingCart = this._pendingCartPageCart;
          const pendingSections = this._pendingCartPageSections;
          this._pendingCartPageCart = null;
          this._pendingCartPageSections = null;
          this.patchCartPage(pendingCart, pendingSections).catch((e) => {
            console.warn('[GWP] Queued cart page patch failed', e);
          });
        }
      }
    }

    setLoading(on, pendingTierKey = '') {
      const sel = document.getElementById('gwp-selector');
      if (!sel) return;

      sel.classList.toggle('gwp-selector--loading', on);
      sel.querySelectorAll('[data-gwp-select-tier]').forEach((btn) => {
        btn.classList.toggle(
          'gwp-selector__card--pending',
          on && btn.dataset.gwpSelectTier === pendingTierKey
        );
        btn.disabled = on;
        btn.setAttribute('aria-busy', on && btn.dataset.gwpSelectTier === pendingTierKey ? 'true' : 'false');
      });

      const status = document.getElementById('gwp-selector-status');
      if (status) {
        const msg = this.config?.messages?.updatingGift || 'Updating your gift…';
        const textEl = status.querySelector('.gwp-selector__status-text');
        if (textEl) textEl.textContent = msg;
      }
    }

    evaluateUpgradePrompt() {
      const promptForcedOff = document.getElementById('gwp-upgrade-prompt');
      if (this.config?.showUpgradePrompt !== true || this.config?.allowCustomerTierChoice !== true) {
        if (promptForcedOff) promptForcedOff.hidden = true;
        this.pendingUpgradeTier = null;
        return;
      }
      const progressEl = document.getElementById('gwp-progress');
      if (!progressEl || !this.config) return;

      const selectedTier = progressEl.dataset.gwpSelectedTier;
      const highestUnlocked = progressEl.dataset.gwpHighestUnlocked;

      if (
        selectedTier &&
        highestUnlocked &&
        highestUnlocked !== selectedTier &&
        highestUnlocked !== this.upgradeDismissedFor
      ) {
        const selIdx = this.config.tiers.findIndex((t) => t.key === selectedTier);
        const highIdx = this.config.tiers.findIndex((t) => t.key === highestUnlocked);
        if (highIdx > selIdx) {
          this.pendingUpgradeTier = this.config.tiers[highIdx];
          this.showUpgradePrompt(this.pendingUpgradeTier);
          return;
        }
      }

      const prompt = document.getElementById('gwp-upgrade-prompt');
      if (prompt) prompt.hidden = true;
    }

    showUpgradePrompt(tierCfg) {
      const prompt = document.getElementById('gwp-upgrade-prompt');
      if (!prompt) return;
      prompt.dataset.gwpUpgradeTo = tierCfg.key;
      prompt.hidden = false;
    }
  }

  /* ====================================================================
     Init
     ==================================================================== */

  const mgr = new GWPManager();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mgr.init());
  } else {
    mgr.init();
  }
})();
